import { z } from 'zod';
import { BIOME_IDS, BiomeIdSchema } from './biomes.js';
import { UPGRADE_MATERIALS } from './upgrade-materials.js';
/**
 * Daily and weekly contracts (ADR 0051): what they may ask, how much, and
 * what they pay. Sizes and pay are set against ten minutes of hunting in the
 * biome aimed at, measured by `pnpm audit:pacing` (hunting only). Starter
 * biome loot was raised later so these rewards now supplement repeatable hunts.
 */
export const DAILY_CONTRACT_KINDS = [
    'kill-mob',
    'kill-biome',
    'camp',
    'upgrade',
    'loot',
    'cast',
];
export const WEEKLY_CONTRACT_KINDS = ['bosses', 'dailies', 'events'];
export const CONTRACT_KINDS = [...DAILY_CONTRACT_KINDS, ...WEEKLY_CONTRACT_KINDS];
/** Three a day, one a week; the client names one by its slot. */
export const DAILY_CONTRACT_SLOTS = ['d0', 'd1', 'd2'];
export const CONTRACT_SLOTS = [...DAILY_CONTRACT_SLOTS, 'w'];
export const ContractSlotSchema = z.enum(CONTRACT_SLOTS);
const RewardItemSchema = z.object({
    templateId: z.string().min(1),
    quantity: z.number().int().min(1),
});
/** A contract as drawn: fixed for its period once drawn, whatever the level does next. */
export const ContractSchema = z.object({
    id: ContractSlotSchema,
    kind: z.enum(CONTRACT_KINDS),
    /** The biome aimed at: its creatures, its camps, its materials in the reward. */
    biome: BiomeIdSchema,
    /** `kill-mob`: the creature. */
    mob: z.string().min(1).optional(),
    /** `bosses`: the zone bosses that count, any `count` of them. */
    bosses: z.array(z.string().min(1)).optional(),
    count: z.number().int().min(1),
    reward: z.object({
        gold: z.number().int().min(0),
        items: z.array(RewardItemSchema),
    }),
});
/** Kills of any of the biome's creatures in ten minutes, rounded down to a round number. */
export const KILL_BIOME_COUNT = {
    meadow: 30,
    forest: 25,
    swamp: 20,
    desert: 20,
    snow: 18,
};
/** One named creature among three or four: half as many. */
export function killMobCount(biome) {
    return Math.ceil(KILL_BIOME_COUNT[biome] / 2);
}
export const CONTRACT_COUNTS = {
    camp: 1,
    /** Attempts at the smith, success or not: a failure costs as much. */
    upgrade: 2,
    loot: 12,
    cast: 25,
    dailies: 10,
    events: 2,
    /** At most this many distinct zone bosses; fewer while fewer are within reach. */
    bosses: 3,
};
/** A daily contract's gold; starter hunts now earn more between claims. */
export const DAILY_CONTRACT_GOLD = {
    meadow: 45,
    forest: 55,
    swamp: 85,
    desert: 115,
    snow: 165,
};
/** The weekly one is worth about five dailies. */
export const WEEKLY_CONTRACT_FACTOR = 5;
/** The smith's materials of the biome, bound to whoever earned them. */
export function dailyContractItems(biome) {
    return [{ templateId: UPGRADE_MATERIALS[biome].common.id, quantity: 2 }];
}
export function weeklyContractItems(biome) {
    return [
        { templateId: UPGRADE_MATERIALS[biome].common.id, quantity: 4 },
        { templateId: UPGRADE_MATERIALS[biome].rare.id, quantity: 1 },
    ];
}
/** The biomes from the capital outwards, the order in which a player meets them. */
export const BIOME_ORDER = BIOME_IDS;
//# sourceMappingURL=contracts.js.map